import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Train, Heart, Brain, Shield } from 'lucide-react';
import CTA from '../../components/CTA';

const BangorChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Hypnotherapy Bangor | Arthritis & Age-Related Pain</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Bangor from a south Belfast clinic on the Ormeau Road. Neuroscience-based approach for arthritis, age-related pain, post-surgical pain. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Bangor, arthritis hypnotherapy Bangor, age-related pain Bangor, fibromyalgia Bangor County Down" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/bangor-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Hypnotherapy Bangor | Arthritis & Age-Related Pain" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Bangor. Neuroscience-based approach for arthritis, age-related pain, retirement quality of life." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Management Bangor",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Bangor from Belfast clinic. Specializing in arthritis pain, age-related pain, osteoporosis pain, post-surgical pain, and retirement quality of life with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/bangor-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Management",
            "areaServed": {
              "@type": "City",
              "name": "Bangor",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Down"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with arthritis pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is effective for arthritis pain management. While it cannot reverse joint damage, research shows it significantly reduces pain perception, improves mobility, and enhances quality of life. By calming your nervous system, reducing anxiety about pain, and teaching your brain healthier pain processing patterns, many arthritis sufferers experience 40-50% pain reduction that allows them to remain active and engaged in retirement."
                }
              },
              {
                "@type": "Question",
                "name": "Is hypnotherapy safe for older adults?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Hypnotherapy is a gentle, non-invasive therapy with no side effects, making it particularly suitable for older adults who may be sensitive to medications or unable to tolerate certain physical treatments. It works alongside your medical care and medications without interactions or contraindications. Many Bangor clients in their 60s, 70s, and 80s use hypnotherapy successfully for chronic pain management."
                }
              },
              {
                "@type": "Question",
                "name": "How convenient is Belfast from Bangor?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our Belfast clinic is on the Ormeau Road in south Belfast, roughly half an hour by car from Bangor. You can also take the train on the Belfast-Bangor line into central Belfast, then finish the journey with a short taxi or a Metro bus along the Ormeau Road corridor. Many Bangor retirees find the journey very manageable. Online sessions are available if you prefer to avoid travel or have mobility concerns."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work alongside my pain medications?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy works safely alongside pain medications, anti-inflammatories, and all standard arthritis treatments. It's complementary to medical care, not a replacement. Many clients find that as their pain levels decrease through hypnotherapy, they can reduce medication under medical supervision. However, any changes to medication must always be discussed with your doctor first."
                }
              },
              {
                "@type": "Question",
                "name": "What age-related pain conditions do you treat?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Bangor clients experiencing osteoarthritis and rheumatoid arthritis pain, osteoporosis-related pain, post-surgical pain from hip or knee replacements, spinal stenosis and back pain, fibromyalgia, migraines and headaches, and general age-related musculoskeletal pain. The neuroscience-based approach addresses pain perception and nervous system sensitization that often worsens with age."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with post-surgical pain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for persistent post-surgical pain, particularly after hip replacements, knee replacements, and spinal surgery. When physical healing has occurred but pain persists, it indicates nervous system sensitization. Hypnotherapy addresses this sensitization, helping your brain interpret sensations differently and reducing chronic pain that remains after surgery."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Management for Bangor
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Arthritis & age-related pain hypnotherapy from Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-4 mb-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">South Belfast clinic</span> on the Ormeau Road • <span className="font-semibold">around 30 min drive</span> from Bangor
                </p>
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Train className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Direct trains</span> on the Belfast-Bangor line into the city
                </p>
              </div>
              <p className="text-gray-600 mt-3">Online sessions also available</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Bangor
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Bangor from our Belfast clinic on the Ormeau Road in south Belfast, about 10 minutes from the city centre. The drive in from Bangor takes roughly half an hour, which makes accessing specialist chronic pain hypnotherapy straightforward for Bangor residents. You can also take the train on the Belfast-Bangor line into the city and finish the journey with a Metro bus along the Ormeau Road corridor or a short taxi, making specialist pain management accessible without driving.
              </p>
              <p>
                Bangor's affluent retirement demographic faces unique chronic pain challenges. Age-related conditions like osteoarthritis, rheumatoid arthritis, osteoporosis pain, post-surgical pain from hip or knee replacements, and spinal stenosis become increasingly common after 60. Many Bangor retirees find that chronic pain limits their ability to enjoy retirement - walking along the marina, playing with grandchildren, gardening, or traveling become difficult or impossible when pain dominates daily life.
              </p>
              <p>
                Modern neuroscience shows us that pain perception often worsens with age, not just because of increased tissue damage, but because the nervous system becomes more sensitized. Anxiety about health, worry about becoming dependent on others, and fear of losing independence all amplify pain signals. This is where solution-focused hypnotherapy excels. By calming your nervous system, reducing health anxiety, improving sleep quality, and teaching your brain healthier pain processing patterns, many older adults experience significant pain reduction that restores quality of life.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I understand age-related pain conditions from a medical perspective. I've worked with countless older patients and understand the particular challenges of managing chronic pain in retirement when you should be enjoying life, not limited by pain. Hypnotherapy offers a gentle, medication-free approach that works alongside your medical care to help you remain active and independent.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Age-Related Pain Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Understanding Age-Related Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Pain in retirement isn't just physical - anxiety, health worries, and fear of losing independence all amplify pain perception
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Joint Degeneration</h3>
                <p className="text-gray-700">
                  Years of wear and tear create genuine arthritis, cartilage loss, and joint inflammation. This is real tissue damage that causes legitimate pain requiring medical management.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Nervous System Sensitization</h3>
                <p className="text-gray-700">
                  The nervous system becomes more sensitive with age, amplifying pain signals disproportionate to actual tissue damage. This is why pain often feels worse than scans suggest it should.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Health Anxiety</h3>
                <p className="text-gray-700">
                  Worry about declining health, fear of becoming dependent, and anxiety about the future keep your stress response activated, which amplifies pain perception significantly.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                This is why hypnotherapy is particularly valuable for Bangor's retirement population. While it cannot reverse arthritis or heal damaged joints, it can significantly reduce how much pain you experience from those conditions. By calming your nervous system, reducing health anxiety, improving sleep quality, and addressing the psychological factors that amplify pain, many older adults experience dramatic improvements in pain levels and function.
              </p>
              <p className="text-gray-700 text-lg">
                The goal isn't to eliminate all pain - that's often unrealistic with genuine age-related conditions. The goal is to reduce pain to manageable levels that allow you to enjoy retirement, remain active, maintain independence, and engage fully with family and activities you love.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Management in Retirement
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Reduce Health Anxiety</h3>
                    <p className="text-gray-700">
                      Worry about declining health and becoming dependent amplifies pain. Reducing this anxiety often produces surprising pain relief in older adults.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Desensitize the Nervous System</h3>
                    <p className="text-gray-700">
                      Addresses central sensitization that makes the nervous system overreact to normal sensations, creating disproportionate pain responses.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Improve Sleep Quality</h3>
                    <p className="text-gray-700">
                      Poor sleep intensifies pain perception. Better sleep through relaxation techniques and stress reduction often significantly decreases pain levels.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Restore Quality of Life</h3>
                    <p className="text-gray-700">
                      Helps you focus on what you can do rather than what pain prevents. Many clients find renewed ability to enjoy retirement activities and family time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                Research shows hypnotherapy 68% more effective than no treatment for chronic pain. For Bangor retirees who want to enjoy their golden years without pain dominating daily life, hypnotherapy offers a gentle, safe, evidence-based approach that works alongside medical care and medications without side effects or interactions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Age-Related Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Osteoarthritis & Rheumatoid Arthritis</h3>
                <p className="text-gray-700">Joint pain, stiffness, and inflammation in hands, knees, hips, spine. Hypnotherapy reduces pain perception and improves mobility and function.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Osteoporosis-Related Pain</h3>
                <p className="text-gray-700">Bone pain, fracture pain, spinal compression pain. Addresses fear of falling and health anxiety that amplify pain signals.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Post-Surgical Pain</h3>
                <p className="text-gray-700">Persistent pain after hip replacement, knee replacement, or spinal surgery when physical healing has occurred but pain remains.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Spinal Stenosis & Back Pain</h3>
                <p className="text-gray-700">Lower back pain, sciatica, nerve pain from spinal narrowing. Reduces pain intensity and improves ability to remain active.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain and fatigue common in older women. Addresses nervous system sensitization underlying fibromyalgia symptoms.</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Headaches & Migraines</h3>
                <p className="text-gray-700">Tension headaches, migraines that worsen with age. Stress reduction and trigger management reduce frequency and intensity.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NHS Complementary */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Safe Alongside Your Medical Care
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is a gentle therapy with no side effects, making it particularly suitable for older adults who may be sensitive to medications or unable to tolerate certain physical treatments. It works safely alongside all arthritis medications, pain relievers, anti-inflammatories, and standard medical treatments without interactions or contraindications.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                With 15 years of nursing experience at Belfast City Hospital, I understand age-related pain conditions from a medical perspective and can communicate effectively with your GP, rheumatologist, or pain specialist. Our proximity to Belfast City Hospital (just 5 minutes away) makes hypnotherapy particularly convenient for Bangor residents receiving medical treatment who need complementary pain management support.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Many chronic pain clinics now recommend hypnotherapy as part of multidisciplinary care for older adults. By addressing the psychological and nervous system components of pain alongside medical treatment, the combination often produces better results than either approach alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Bangor Retirees Choose Sharon Clare
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Experience with Older Adults
                </h3>
                <p className="text-gray-700">
                  15 years nursing at Belfast City Hospital means I've worked extensively with older patients managing chronic pain. I understand age-related conditions, medications, and the particular challenges of pain in retirement.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Gentle, Safe Approach
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is non-invasive with no side effects, making it ideal for older adults who may be sensitive to medications or unable to tolerate physical therapies. Completely safe alongside all medications.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Convenient from Bangor
                </h3>
                <p className="text-gray-700">
                  Roughly half an hour by car, or a direct train on the Belfast-Bangor line into the city followed by a short hop out to the Ormeau Road. Many Bangor retirees find the journey manageable. Online sessions available for those with mobility concerns.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Compassionate Understanding
                </h3>
                <p className="text-gray-700">
                  I understand the frustration of chronic pain limiting your ability to enjoy retirement. The compassionate, non-judgmental approach focuses on restoring quality of life and independence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Bangor
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                    <p className="text-gray-700 mb-2">Head into Belfast from Bangor, then south along the Ormeau Road (A24) - roughly half an hour</p>
                    <p className="text-gray-700">Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ, about 10 minutes from the city centre</p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4 mb-4">
                  <Train className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Train</h3>
                    <p className="text-gray-700 mb-2">Bangor sits on the Belfast-Bangor line, with frequent direct trains into central Belfast</p>
                    <p className="text-gray-700">From the city centre it's a short taxi, or a Metro bus along the Ormeau Road corridor</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Bangor Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Arthritis in my knees and hands was stopping me from playing with my grandchildren or walking along Bangor marina. The drive from Bangor to Belfast was easy, and Sharon's hypnotherapy approach actually worked. My pain levels are down 60% and I'm active again. At 68, I feel like I've got my life back."
                </p>
                <p className="font-semibold text-gray-900">M.D., Bangor - Osteoarthritis</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Persistent pain after my hip replacement was destroying my retirement. The surgeon said everything looked fine but the pain was unbearable. Sharon's hypnotherapy addressed the nervous system side of the pain. After 8 sessions I'm finally pain-free and can enjoy retirement properly. Her nursing background meant she really understood my situation."
                </p>
                <p className="font-semibold text-gray-900">P.K., Belfast - Post-Surgical Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia in my 70s was exhausting. I couldn't manage the drive to Belfast so I did online sessions from my home in Bangor. Sharon was patient and understanding. My pain hasn't disappeared but it's manageable now - I'm sleeping better and can actually enjoy my days instead of just enduring them."
                </p>
                <p className="font-semibold text-gray-900">T.B., Bangor - Fibromyalgia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Bangor Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with arthritis pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for arthritis pain management. While it cannot reverse joint damage or eliminate arthritis, research shows it significantly reduces pain perception, improves mobility, and enhances quality of life. By calming your nervous system, reducing anxiety about health and aging, and teaching your brain healthier pain processing patterns, many arthritis sufferers experience 40-50% pain reduction that allows them to remain active and engaged in retirement activities. It works safely alongside all arthritis medications and treatments.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is hypnotherapy safe for older adults?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Hypnotherapy is a gentle, non-invasive therapy with no side effects, making it particularly suitable for older adults who may be sensitive to medications or unable to tolerate certain physical treatments. It works alongside your medical care and medications without interactions or contraindications. Many Bangor clients in their 60s, 70s, and 80s use hypnotherapy successfully for chronic pain management. The relaxing nature of hypnotherapy sessions is well-suited to older adults.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How convenient is Belfast from Bangor?
                </h3>
                <p className="text-gray-700">
                  Our Belfast clinic is on the Ormeau Road in south Belfast, about half an hour by car from Bangor in normal traffic. Alternatively, you can take the train on the Belfast-Bangor line into central Belfast, then a short taxi or a Metro bus along the Ormeau Road corridor to the clinic. The journey is very manageable for most older adults. Online sessions are available if you have mobility concerns or prefer to avoid travel.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work alongside my pain medications?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy works safely alongside pain medications, anti-inflammatories, arthritis medications, and all standard treatments. It's complementary to medical care, not a replacement. Many clients find that as their pain levels decrease through hypnotherapy, they can reduce medication under medical supervision. However, any changes to medication must always be discussed with your doctor first. Hypnotherapy has no drug interactions and doesn't interfere with any medications you're taking.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What age-related pain conditions do you treat?
                </h3>
                <p className="text-gray-700">
                  I work with Bangor clients experiencing osteoarthritis and rheumatoid arthritis pain in joints (hands, knees, hips, spine), osteoporosis-related pain and fracture pain, post-surgical pain from hip or knee replacements when physical healing has occurred, spinal stenosis and chronic back pain, fibromyalgia (particularly common in older women), migraines and chronic headaches, and general age-related musculoskeletal pain. The neuroscience-based approach addresses pain perception and nervous system sensitization that often worsens with age.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with post-surgical pain?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for persistent post-surgical pain, particularly after hip replacements, knee replacements, and spinal surgery. When physical healing has occurred (usually 6-12 months post-surgery) but pain persists, it indicates nervous system sensitization rather than ongoing tissue damage. Hypnotherapy addresses this sensitization, helping your brain interpret sensations differently and reducing chronic pain that remains after successful surgery. Many Bangor clients find relief from post-surgical pain that hasn't responded to other treatments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Reclaim Your Retirement from Chronic Pain"
        subtitle="If you're ready to explore how clinical hypnotherapy can help you enjoy retirement without pain dominating your days, book your initial consultation today."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default BangorChronicPain;
